import { useMemo } from 'react';

interface PeakUsagePoint {
  hour_of_day: number;
  day_of_week: number;
  message_count: number;
}

interface PeakInfo {
  hour: number;
  day: number;
  dayName: string;
  messageCount: number;
}

interface PeakUsageHeatmapProps {
  data: PeakUsagePoint[];
  peak: PeakInfo;
  loading?: boolean;
  title?: string;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

// Format hour for display
const formatHour = (hour: number) => {
  if (hour === 0) return '12a';
  if (hour === 12) return '12p';
  if (hour < 12) return `${hour}a`;
  return `${hour - 12}p`;
};

// Get color intensity based on value
const getColor = (value: number, maxValue: number) => {
  if (value === 0 || maxValue === 0) return 'bg-gray-100';

  const intensity = value / maxValue;

  if (intensity > 0.8) return 'bg-blue-600';
  if (intensity > 0.6) return 'bg-blue-500';
  if (intensity > 0.4) return 'bg-blue-400';
  if (intensity > 0.2) return 'bg-blue-300';
  if (intensity > 0.1) return 'bg-blue-200';
  return 'bg-blue-100';
};

export function PeakUsageHeatmap({
  data,
  peak,
  loading,
  title = 'Peak Usage Times',
}: PeakUsageHeatmapProps) {
  // Create a lookup map for quick access
  const dataMap = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((point) => {
      map.set(`${point.day_of_week}-${point.hour_of_day}`, point.message_count);
    });
    return map;
  }, [data]);

  // Find max value for color scaling
  const maxValue = useMemo(() => {
    return Math.max(...data.map((p) => p.message_count), 0);
  }, [data]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-pulse">
            <div className="w-full h-48 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          <p>No usage data available for this period</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {peak.messageCount > 0 && (
          <div className="text-sm text-gray-500">
            Peak: {peak.dayName} at {formatHour(peak.hour)} ({peak.messageCount.toLocaleString()} msgs)
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          {/* Hour labels */}
          <div className="flex mb-1">
            <div className="w-12" /> {/* Spacer for day labels */}
            {HOURS.filter((_, i) => i % 2 === 0).map((hour) => (
              <div
                key={hour}
                className="flex-1 text-center text-xs text-gray-500"
                style={{ minWidth: '24px' }}
              >
                {formatHour(hour)}
              </div>
            ))}
          </div>

          {/* Heatmap grid */}
          {DAYS.map((day, dayIndex) => (
            <div key={day} className="flex items-center mb-1">
              <div className="w-12 text-xs text-gray-500 text-right pr-2">
                {day}
              </div>
              <div className="flex-1 flex gap-0.5">
                {HOURS.map((hour) => {
                  const value = dataMap.get(`${dayIndex}-${hour}`) || 0;
                  const isPeak = dayIndex === peak.day && hour === peak.hour;

                  return (
                    <div
                      key={hour}
                      className={`flex-1 h-6 rounded-sm ${getColor(value, maxValue)} ${
                        isPeak ? 'ring-2 ring-blue-600 ring-offset-1' : ''
                      }`}
                      style={{ minWidth: '12px' }}
                      title={`${day} ${formatHour(hour)}: ${value.toLocaleString()} messages`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Color legend */}
      <div className="mt-4 flex items-center justify-center gap-2">
        <span className="text-xs text-gray-500">Less</span>
        <div className="flex gap-1">
          <div className="w-4 h-4 bg-gray-100 rounded-sm" />
          <div className="w-4 h-4 bg-blue-100 rounded-sm" />
          <div className="w-4 h-4 bg-blue-200 rounded-sm" />
          <div className="w-4 h-4 bg-blue-300 rounded-sm" />
          <div className="w-4 h-4 bg-blue-400 rounded-sm" />
          <div className="w-4 h-4 bg-blue-500 rounded-sm" />
          <div className="w-4 h-4 bg-blue-600 rounded-sm" />
        </div>
        <span className="text-xs text-gray-500">More</span>
      </div>
    </div>
  );
}
